import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeeService } from '../service/fee.service';
import { MemberProfileResponse, MemberSubgroupInfo } from '../interface/fee.interface';
import { DiscountType } from '../enums/DiscountType';
import { handleDiscountKeyDown, integerValidator, sanitizeDiscountPaste, syncDiscountControlErrors } from '../utils/fee-discount.utils';

export interface ApplyDiscountDialogData {
  memberId: number;
  subgroup: MemberSubgroupInfo;
}

@Component({
  selector: 'app-fee-apply-discount-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './fee-apply-discount-dialog.component.html',
  styleUrls: ['./fee-apply-discount-dialog.component.scss']
})
export class FeeApplyDiscountDialogComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private discountTypeBeforeOpen = '';

  form!: FormGroup;
  finalPrice = 0;
  submitting = false;

  readonly discountTypes = Object.values(DiscountType);

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<FeeApplyDiscountDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: ApplyDiscountDialogData,
    private readonly feeService: FeeService
  ) {}

  get basePrice(): number {
    return this.data.subgroup.price ?? 0;
  }

  ngOnInit(): void {
    const sg = this.data.subgroup;
    this.form = this.fb.group({
      discountType: [sg.discountType ?? DiscountType.PERCENT],
      discountValue: [sg.discountValue ?? null, [Validators.min(0), integerValidator()]],
      discountReason: [sg.discountReason ?? '', Validators.maxLength(100)],
    });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncDiscountErrors();
        this.recalculateFinalPrice();
      });
    this.recalculateFinalPrice();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDiscountKeyDown(event: KeyboardEvent): void {
    handleDiscountKeyDown(event);
  }

  onDiscountPaste(event: ClipboardEvent): void {
    this.form.get('discountValue')!.setValue(sanitizeDiscountPaste(event));
  }

  onDiscountSelectOpened(opened: boolean): void {
    if (opened) {
      this.discountTypeBeforeOpen = this.form.get('discountType')!.value || '';
    }
  }

  onDiscountTypeOptionClick(value: string): void {
    if (this.discountTypeBeforeOpen === value) {
      setTimeout(() => this.form.get('discountType')!.setValue(DiscountType.PERCENT));
    }
  }

  discountTypeSymbol(type: string | null | undefined): string {
    return type === DiscountType.AMD ? '֏' : '%';
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.submitting) {
      return;
    }

    const discountValue = Number(this.form.get('discountValue')!.value) || 0;
    const discountType = this.form.get('discountType')!.value;
    const discountReason = (this.form.get('discountReason')!.value as string)?.trim() || null;
    const hasDiscount = discountValue > 0 && !!discountType;
    const hasReason = !!discountReason;

    if (!hasDiscount && !hasReason) {
      this.dialogRef.close();
      return;
    }

    this.submitting = true;
    this.feeService.updateEnrollmentDiscount(this.data.memberId, this.data.subgroup.id, {
      discountType: hasDiscount ? discountType : null,
      discountValue: hasDiscount ? discountValue : null,
      discountReason,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: profile => this.dialogRef.close(profile),
        error: () => { this.submitting = false; }
      });
  }

  private syncDiscountErrors(): void {
    syncDiscountControlErrors(
      this.form.get('discountType')!,
      this.form.get('discountValue')!,
      this.basePrice
    );
  }

  private recalculateFinalPrice(): void {
    const price = this.basePrice;
    const discountType: string = this.form.get('discountType')!.value;
    const discountValue: number = Number(this.form.get('discountValue')!.value) || 0;

    if (!discountValue || !discountType) {
      this.finalPrice = price;
      return;
    }
    if (discountType === DiscountType.PERCENT) {
      this.finalPrice = price - (price * discountValue / 100);
    } else {
      this.finalPrice = price - discountValue;
    }
  }
}
